import { cn } from '../lib/utils';
import { useElementWidth } from '../hooks/use-element-width';
import { BackgroundTint, DesignLayers } from './design/DesignLayers';
import { backgroundImageStyle } from '../utils/invitationStyle';
import { coverPerspective, perspectiveForBackground, perspectiveSurfaceStyle } from '../utils/perspective';


// Invitations are designed on a US Letter page at 96 DPI (8.5" x 11").
// Font sizes are stored relative to that width, so previews scale text by
// (rendered width / LETTER_WIDTH) to look the same at any size.
export const LETTER_WIDTH = 816;
export const LETTER_ASPECT = '8.5 / 11';

/**
 * Read-only, responsive rendering of an invitation: background image plus
 * positioned text fields. Used on the share page, the dashboard and
 * template cards.
 */
export const InvitationPreview = ({
  backgroundImage,
  fields = [],
  elements = [],
  background = null,
  perspective = null, // template.perspective: side-view card, the text is warped onto it
  getText,
  showWatermark = false,
  aspectRatio = LETTER_ASPECT,
  textShadow,
  alt = 'Invitation preview',
  className,
  imgClassName,
  eager = false,
  ...props
}) => {
  const [ref, width] = useElementWidth();
  const scale = width ? width / LETTER_WIDTH : 0;
  const [aw, ah] = String(aspectRatio).split('/').map(Number);
  // corners are % of the letter-shaped photo; a 5:7 card crops that photo (object-cover), so move them with it
  const surface =
    perspective && width && aw && ah ? perspectiveSurfaceStyle(coverPerspective(perspectiveForBackground(perspective, background), aw / ah), width, (width * ah) / aw) : null;

  return (
    <div
      ref={ref}
      className={cn('relative overflow-hidden bg-cream', className)}
      style={{ aspectRatio, isolation: 'isolate' }}
      {...props}
    >
      {backgroundImage ? (
        <img
          src={backgroundImage}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className={cn('absolute inset-0 h-full w-full object-cover', imgClassName)}
          style={backgroundImageStyle(background, scale)}
        />
      ) : (
        <span className="sr-only">{alt}</span>
      )}

      <BackgroundTint background={background} />

      {scale > 0 && (() => {
        const layers = (
          <DesignLayers
            fields={Array.isArray(fields) ? fields : []}
            elements={Array.isArray(elements) ? elements : []}
            scale={scale}
            getText={getText}
            textShadow={textShadow}
          />
        );
        return surface ? <div style={surface}>{layers}</div> : layers;
      })()}

      {showWatermark && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10" aria-hidden="true">
          <div className="w-full h-full flex flex-wrap items-center justify-around content-around">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className="font-bold -rotate-[30deg] select-none"
                style={{
                  color: 'rgba(128, 128, 128, 0.35)',
                  fontSize: `${Math.max(14, 36 * (scale || 0.5))}px`,
                }}
              >
                PREVIEW
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationPreview;
